import { ChangeEvent, FC, FormEvent, useState } from 'react';

import { useRouter } from 'next/router';

import * as Yup from 'yup';
import {
  IoArrowBack,
  IoLogoWhatsapp,
  IoMailOutline,
  IoPhonePortraitOutline,
  IoOptions,
} from 'react-icons/io5';
import { useToast } from '@chakra-ui/react';

import { maskCellPhone, unmask } from '~/shared/utils/masks';
import { routes } from '~/shared/constants/routes';
import { Input, InputProps } from '~/shared/components/Form/Input';
import { FormGroup } from '~/shared/components/Form/FormGroup';
import { Button } from '~/shared/components/Form/Button';

import { AccountRecoveryOption, useAuth } from '../../hooks/auth';
import { Link } from '../../components/Link';
import { Header } from '../../components/Header';
import { ButtonLink } from '../../components/ButtonLink';

import { AccountRecoveryOptions } from './styles';

export const ForgotPassword: FC = () => {
  const { sendRecoveryAccountLink } = useAuth();

  const toast = useToast();
  const router = useRouter();

  const [accountRecoveryOption, setAccountRecoveryOption] =
    useState<AccountRecoveryOption | null>(null);
  const [inputOptions, setInputOptions] = useState({} as InputProps);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');

  const [isRecoveringAccount, setIsRecoveringAccount] = useState(false);

  function handleSelectAccountRecoveryOption(
    option: AccountRecoveryOption
  ): void {
    setAccountRecoveryOption(option);

    switch (option) {
      case 'email':
        setInputOptions({
          placeholder: 'Digite seu e-mail',
          name: 'email',
          autoComplete: 'email',
          startIcon: IoMailOutline,
        });
        break;

      case 'sms':
        setInputOptions({
          name: 'sms',
          placeholder: 'Digite seu número de celular',
          autoComplete: 'tel',
          maxLength: 16,
          startIcon: IoPhonePortraitOutline,
        });
        break;

      case 'whatsapp':
        setInputOptions({
          name: 'whatsapp',
          placeholder: 'Digite seu número de celular',
          autoComplete: 'tel',
          startIcon: IoLogoWhatsapp,
          maxLength: 16,
        });
        break;

      default:
        break;
    }
  }

  function handleChangeAccountRecoveryOption(): void {
    setAccountRecoveryOption(null);
    setInputValue('');
    setInputOptions({} as InputProps);
  }

  function handleChangeInput(event: ChangeEvent<HTMLInputElement>): void {
    const { value } = event.target;

    let newValue = value;

    if (accountRecoveryOption !== 'email') {
      newValue = unmask(newValue);
    }

    setInputError('');

    setInputValue(newValue);
  }

  async function handleRecoveryAccount(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    if (!accountRecoveryOption) return;

    try {
      setIsRecoveringAccount(true);

      if (accountRecoveryOption === 'email') {
        await Yup.string()
          .email('e-mail inválido')
          .required('e-mail é obrigatório')
          .validate(inputValue);
      } else {
        await Yup.string()
          .required('número de celular é obrigatório')
          .validate(inputValue);
      }

      await sendRecoveryAccountLink({
        option: accountRecoveryOption,
        value: inputValue,
      });

      let description = 'Abra o link que enviamos por SMS.';

      if (accountRecoveryOption === 'email') {
        description = 'Abra o link que enviamos no seu e-mail.';
      }

      if (accountRecoveryOption === 'whatsapp') {
        description = 'Abra o link que enviamos no seu Whatsapp.';
      }

      toast({
        title: 'Boa, seu link de recuperação foi enviado!',
        description,
        status: 'success',
        isClosable: true,
        position: 'top-right',
      });

      router.push(routes.auth.signIn);
    } catch (error) {
      setIsRecoveringAccount(false);

      if (error instanceof Yup.ValidationError) {
        setInputError(error.message);

        return;
      }

      if (error.response?.status === 404) {
        setInputError(
          accountRecoveryOption === 'email'
            ? 'e-mail não cadastrado'
            : 'celular não cadastrado'
        );

        return;
      }

      toast({
        title: 'Ops, ocorreu um erro ao enviar o link de recuperação!',
        description: 'Recarregue a página e tente novamente.',
        status: 'error',
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  return (
    <>
      <form onSubmit={handleRecoveryAccount}>
        <Header
          subtitle={
            !accountRecoveryOption
              ? 'selecione uma opção abaixo para enviarmos o código de recuperação.'
              : accountRecoveryOption !== 'email'
                ? 'digite seu número de celular para recuperar sua conta.'
                : 'digite seu e-mail para recuperar sua conta.'
          }
        />

        {accountRecoveryOption ? (
          <>
            <FormGroup>
              <Input
                onChange={handleChangeInput}
                variant="default"
                error={inputError}
                value={
                  accountRecoveryOption !== 'email'
                    ? maskCellPhone(inputValue)
                    : inputValue
                }
                {...inputOptions}
              />
            </FormGroup>

            <FormGroup>
              <Button
                disabledTooltip={inputOptions.placeholder}
                isLoading={isRecoveringAccount}
                type="submit"
                disabled={
                  !!inputError ||
                  (accountRecoveryOption !== 'email'
                    ? inputValue.length < 11
                    : !inputValue)
                }
              >
                Recuperar
              </Button>
            </FormGroup>
          </>
        ) : (
          <AccountRecoveryOptions>
            <FormGroup>
              <Button
                onClick={() => handleSelectAccountRecoveryOption('whatsapp')}
                backgroundColor="secondary"
                textColor="onSecondary"
              >
                Receber pelo Whatsapp
              </Button>
            </FormGroup>

            <FormGroup>
              <Button
                onClick={() => handleSelectAccountRecoveryOption('email')}
                backgroundColor="secondary"
                textColor="onSecondary"
              >
                Receber por e-mail
              </Button>
            </FormGroup>

            <FormGroup>
              <Button
                onClick={() => handleSelectAccountRecoveryOption('sms')}
                backgroundColor="secondary"
                textColor="onSecondary"
              >
                Receber por SMS
              </Button>
            </FormGroup>
          </AccountRecoveryOptions>
        )}
      </form>

      {accountRecoveryOption && (
        <ButtonLink
          onClick={handleChangeAccountRecoveryOption}
          title="trocar opção de recuperação"
          icon={IoOptions}
        />
      )}

      <Link
        href={routes.auth.signIn}
        title="entrar com a minha conta"
        icon={IoArrowBack}
      />
    </>
  );
};
